import type { ComponentProps } from "react";
import { useCallback, useMemo, useRef, useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { partition } from "lodash-es";
import { ErrorCode, FileRejection, useDropzone } from "react-dropzone";
import { twJoin } from "tailwind-merge";

import { useUploadFile } from "src/api/filesApi/queries";
import { FileCard } from "src/base-components/FileCard";
import { UploadFileEnvFilesPostEnvEnum } from "src/clients/files";
import { Button } from "src/design-system/Button";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { Environment } from "src/hooks/useEnvironment";
import {
  AcceptedFileTypes,
  DEFAULT_ACCEPTED_FILE_TYPES,
  FileSource,
  getExtensionsFromAcceptedFileTypes,
  humanReadableSize,
  MAX_FILE_SIZE_BYTES,
} from "src/utils/files";
import { pluralizeWord } from "src/utils/stringUtils";

export type FileUploadFileItem = {
  key: string;
  fileId?: string;
  env?: Environment;
  file?: File;
  onDelete?: () => void;
};

type FileUploadProps = {
  /** Called when a file passes validation and is accepted by the dropzone. */
  onFileSelect: (file: File) => void;
  /** Allow selecting multiple files. When false, the upload button hides after one file. */
  multiple?: boolean;
  /** Files currently being uploaded — rendered as FileCards with a spinner. */
  uploadingFiles?: File[];
  /** Uploaded files to display as FileCards. */
  files?: FileUploadFileItem[];
  /** Disables the upload button and hides delete actions. */
  disabled?: boolean;
  /** Helper text shown above the file list (e.g. accepted formats, size limit). */
  description?: string;
  /** MIME-type-to-extensions map for the file picker. Defaults to all supported types. */
  accept?: AcceptedFileTypes;
  /** Maximum file size in bytes. Defaults to MAX_FILE_SIZE_BYTES (32 MB). */
  maxSize?: number;
  /** Maximum number of files. When reached, the upload button is disabled and the dropzone stops accepting files. */
  maxFiles?: number;
  /**
   * Called when files are rejected by dropzone validation.
   * Defaults to showing an error toast per rejection.
   */
  onFileRejected?: (rejections: FileRejection[]) => void;
  dataLoc?: string;
  floatingWindowPlacement?: ComponentProps<
    typeof FileCard
  >["floatingWindowPlacement"];
  floatingWindowOffset?: ComponentProps<
    typeof FileCard
  >["floatingWindowOffset"];
};

const getDropzoneRejectionDescription = (
  fileName: string,
  errorCode: ErrorCode | string,
  accept: AcceptedFileTypes,
  maxSize: number,
  maxFiles?: number,
): string => {
  const extensions = getExtensionsFromAcceptedFileTypes(accept);
  switch (errorCode) {
    case ErrorCode.FileInvalidType:
      return `Upload failed: '${fileName}' is not a supported file type. Supported types: ${extensions.join(", ")}.`;
    case ErrorCode.FileTooLarge:
      return `Upload failed: '${fileName}' exceeds the maximum file size limit of ${humanReadableSize(maxSize)}.`;
    case ErrorCode.TooManyFiles:
      return maxFiles === undefined
        ? "Upload failed: too many files selected."
        : `Upload failed: you can upload up to ${maxFiles} more ${pluralizeWord(maxFiles, "file")}.`;
    default:
      return `Upload failed for '${fileName}'.`;
  }
};

const createFileExtensionValidator =
  (accept: AcceptedFileTypes) => (file: File) => {
    if ("name" in file) {
      const extension = file.name.split(".").pop()?.toLowerCase();
      if (!extension) {
        return {
          code: "FileExtensionMissing",
          message: "File must have an extension. E.g. .pdf, .jpg, or .png",
        };
      }
      const allowedExtensions = getExtensionsFromAcceptedFileTypes(accept);
      if (!allowedExtensions.includes(extension)) {
        return {
          code: ErrorCode.FileInvalidType,
          message: `Invalid file type. Supported types: ${allowedExtensions.join(", ")}`,
        };
      }
    }
    return null;
  };

const defaultOnUploadError = (file: File, retry: () => void) => {
  toastActions.failure({
    title: "File Upload Failed",
    description: `Upload failed for '${file.name}'. Please try again.`,
    actionText: "Try again",
    onActionClick: retry,
  });
};

const defaultOnFileRejected = (
  rejections: FileRejection[],
  accept: AcceptedFileTypes,
  maxSize: number,
  maxFiles?: number,
) => {
  // Exceeding the file limit rejects every dropped file with the same error —
  // collapse those into a single toast instead of one per file.
  const [tooManyFiles, otherRejections] = partition(
    rejections,
    (rejection) => rejection.errors[0].code === ErrorCode.TooManyFiles,
  );

  if (tooManyFiles.length > 0) {
    toastActions.failure({
      title: "File Upload Failed",
      description: getDropzoneRejectionDescription(
        tooManyFiles[0].file.name,
        ErrorCode.TooManyFiles,
        accept,
        maxSize,
        maxFiles,
      ),
    });
  }

  otherRejections.forEach((rejection) => {
    const description = getDropzoneRejectionDescription(
      rejection.file.name,
      rejection.errors[0].code,
      accept,
      maxSize,
      maxFiles,
    );
    toastActions.failure({ title: "File Upload Failed", description });
  });
};

export const useFileUploadDropzone = ({
  onFileSelect,
  multiple,
  disabled,
  accept,
  maxSize,
  maxFiles,
  onFileRejected,
}: {
  onFileSelect: (file: File) => void;
  multiple: boolean;
  disabled: boolean;
  accept: AcceptedFileTypes;
  maxSize: number;
  maxFiles?: number;
  onFileRejected?: (rejections: FileRejection[]) => void;
}) => {
  const handleDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      for (const file of acceptedFiles) {
        onFileSelect(file);
      }
      if (rejectedFiles.length > 0) {
        if (onFileRejected) {
          onFileRejected(rejectedFiles);
        } else {
          defaultOnFileRejected(rejectedFiles, accept, maxSize, maxFiles);
        }
      }
    },
    [onFileSelect, accept, maxSize, maxFiles, onFileRejected],
  );

  return useDropzone({
    onDrop: handleDrop,
    useFsAccessApi: true,
    maxFiles: multiple ? maxFiles : 1,
    multiple,
    maxSize,
    accept,
    validator: createFileExtensionValidator(accept),
    disabled,
    noClick: true,
    noKeyboard: true,
  });
};

export const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  multiple = false,
  uploadingFiles,
  files,
  disabled = false,
  description,
  accept = DEFAULT_ACCEPTED_FILE_TYPES,
  maxSize = MAX_FILE_SIZE_BYTES,
  maxFiles,
  onFileRejected,
  dataLoc,
  floatingWindowPlacement,
  floatingWindowOffset,
}) => {
  const fileCount = (files?.length ?? 0) + (uploadingFiles?.length ?? 0);
  const maxFilesReached = maxFiles !== undefined && fileCount >= maxFiles;
  const showUploadButton = multiple || fileCount === 0;

  const { getRootProps, getInputProps, isDragActive, open } =
    useFileUploadDropzone({
      onFileSelect,
      multiple,
      disabled: disabled || !showUploadButton || maxFilesReached,
      accept,
      maxSize,
      maxFiles: maxFiles !== undefined ? maxFiles - fileCount : undefined,
      onFileRejected,
    });

  return (
    <div
      {...getRootProps({
        "data-loc": dataLoc,
        className: twJoin(
          "flex flex-col gap-2 rounded-lg border border-transparent border-dashed transition-colors",
          isDragActive && "border-indigo-400 bg-indigo-50",
        ),
      })}
    >
      <input
        {...getInputProps()}
        data-loc={dataLoc ? `${dataLoc}-input` : undefined}
      />
      {description && (
        <span className="font-inter-normal-12px text-gray-500">
          {description}
        </span>
      )}
      {files?.map((item) => {
        const itemDataLoc = dataLoc ? `${dataLoc}-file-${item.key}` : undefined;
        if (item.fileId && item.env) {
          return (
            <FileCard
              key={item.key}
              dataLoc={itemDataLoc}
              disabled={disabled}
              file={item.file}
              fileEnv={item.env}
              fileId={item.fileId}
              floatingWindowOffset={floatingWindowOffset}
              floatingWindowPlacement={floatingWindowPlacement}
              onDelete={item.onDelete}
            />
          );
        }
        if (item.file) {
          return (
            <FileCard
              key={item.key}
              dataLoc={itemDataLoc}
              disabled={disabled}
              file={item.file}
              floatingWindowOffset={floatingWindowOffset}
              floatingWindowPlacement={floatingWindowPlacement}
              onDelete={item.onDelete}
            />
          );
        }
        return null;
      })}
      {uploadingFiles?.map((file, index) => (
        <FileCard
          key={`uploading-${file.name}-${index}`}
          dataLoc={dataLoc ? `${dataLoc}-uploading-${index}` : undefined}
          file={file}
          isUploading
        />
      ))}
      {showUploadButton && (
        <div className="w-fit">
          <Tooltip
            disabled={!maxFilesReached}
            title={`You can upload up to ${maxFiles} ${pluralizeWord(maxFiles ?? 0, "file")}`}
            asChild
          >
            <Button
              dataLoc={dataLoc ? `${dataLoc}-upload-btn` : undefined}
              isDisabled={disabled || maxFilesReached}
              iconLeft={faPlus}
              size="sm"
              variant="secondary"
              onClick={open}
            >
              {multiple ? "Upload files" : "Upload file"}
            </Button>
          </Tooltip>
        </div>
      )}
    </div>
  );
};

type UseFileUploadOptions = {
  multiple?: boolean;
  env: Environment;
  source: FileSource;
  value?: string | string[];
  onChange?: (value: string | string[] | null) => void;
  disabled?: boolean;
  /** Called when a file upload fails. Defaults to showing an error toast with retry. */
  onUploadError?: (file: File, retry: () => void) => void;
};

type UseFileUploadReturn = {
  fileUploadProps: FileUploadProps;
};

/**
 * Headless hook that manages file upload state and API integration.
 * Returns props to spread onto <FileUpload> — handles upload, tracking,
 * optimistic UI (uploading spinners), and cleanup on failure.
 */
export const useFileUpload = ({
  multiple = false,
  env,
  source,
  value,
  onChange,
  disabled = false,
  onUploadError,
}: UseFileUploadOptions): UseFileUploadReturn => {
  const { onFileSelect, onFileUpload, onFileRemove, getFile } = useUploadFile();
  const [uploadingFiles, setUploadingFiles] = useState<File[]>([]);

  const fileIds: string[] = useMemo(() => {
    if (Array.isArray(value)) return value;
    if (value) return [value];
    return [];
  }, [value]);

  const fileIdsRef = useRef(fileIds);
  fileIdsRef.current = fileIds;

  const handleFileSelect = useCallback(
    async (file: File) => {
      const uploadKey = `file-upload-${source}-${Date.now()}-${file.name}`;
      onFileSelect(file, uploadKey);
      setUploadingFiles((prev) => [...prev, file]);

      try {
        const id = await onFileUpload(
          file,
          uploadKey,
          env as UploadFileEnvFilesPostEnvEnum,
          source,
        );
        if (id) {
          // Re-register under ID-based key so getFile can find it later
          onFileSelect(file, `file-upload-${source}-${id}`);
          if (multiple) {
            const newIds = [...fileIdsRef.current, id];
            onChange?.(newIds);
          } else {
            onChange?.(id);
          }
        }
      } catch {
        const retry = () => handleFileSelect(file);
        if (onUploadError) {
          onUploadError(file, retry);
        } else {
          defaultOnUploadError(file, retry);
        }
        onFileRemove(uploadKey);
      } finally {
        setUploadingFiles((prev) => prev.filter((f) => f !== file));
      }
    },
    [
      onFileSelect,
      onFileUpload,
      onFileRemove,
      env,
      source,
      multiple,
      onChange,
      onUploadError,
    ],
  );

  const handleRemove = useCallback(
    (idToRemove: string) => {
      if (multiple) {
        const newIds = fileIdsRef.current.filter((id) => id !== idToRemove);
        onChange?.(newIds.length > 0 ? newIds : []);
      } else {
        onChange?.(null);
      }
    },
    [multiple, onChange],
  );

  const files: FileUploadFileItem[] = fileIds.map((id) => ({
    key: id,
    fileId: id,
    env,
    file: getFile(`file-upload-${source}-${id}`) ?? undefined,
    onDelete: () => handleRemove(id),
  }));

  return {
    fileUploadProps: {
      onFileSelect: handleFileSelect,
      multiple,
      uploadingFiles,
      disabled,
      files,
    },
  };
};
